import { create } from "zustand";
import { apiClient } from "@/api/client";
import useAuthStore from "@/store/authStore";

interface CompanyDetailsState {
  isLoading: boolean;
  updateCompanyDetails: (data: CompanyDetailsUpdate, logoFile?: File) => Promise<void>;
}

interface CompanyDetailsUpdate {
  name: string;
  phone: string;
  main_email: string;
  website: string;
  billing_address: {
    address_line_1: string;
    address_line_2: string;
    country: string;
    state: string;
    zip_postal: string;
  };
}

const useCompanyDetailsStore = create<CompanyDetailsState>((set) => ({
  isLoading: false,

  updateCompanyDetails: async (data: CompanyDetailsUpdate, logoFile?: File) => {
    set({ isLoading: true });
    try {
      const media_agency_information = useAuthStore.getState().media_agency_information;
      
      if (!media_agency_information?.id) {
        throw new Error("No media agency information found");
      }

      let logoKey: string | undefined;
      let logoMime: string | undefined;

      // Step 1: Upload logo if provided
      if (logoFile) {
        const ext = logoFile.name.split('.').pop() || 'jpg';
        const mime = logoFile.type;

        // Get presigned URL
        const initiateResponse = await apiClient.post(
          `/media-agency/${media_agency_information.id}/logo/initiate`,
          {
            ext,
            mime,
            size: logoFile.size,
          }
        );

        const { url, headers, key } = initiateResponse.data.data;

        // Upload to S3
        await fetch(url, {
          method: 'PUT',
          headers: headers,
          body: logoFile,
        });

        logoKey = key;
        logoMime = mime;
      }

      // Step 2: Update company details
      const updatePayload: any = {
        name: data.name,
        phone: data.phone,
        main_email: data.main_email,
        website: data.website,
        billing_address: data.billing_address,
      };

      if (logoKey && logoMime) {
        updatePayload.logo_key = logoKey;
        updatePayload.logo_mime = logoMime;
      }

      const response = await apiClient.put(
        `/media-agency-information/${media_agency_information.id}`,
        updatePayload
      );

      // Update auth store with new data
      const updatedAgencyInfo = response.data.data;
      useAuthStore.getState().setMediaAgencyInformation(updatedAgencyInfo);

      set({ isLoading: false });
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },
}));

export default useCompanyDetailsStore;
